import type {
  GetApiResponseByStatus,
  GetApiResponseByUrl,
} from '@crossbeam/openapi';

import { crossbeamApi } from '@/api';
import type { Source } from '@/types/sources';

import type {
  CreateAccountProductSourceRequest,
  ObjectFieldsResponse,
} from '../types';

export type ApiResult<TData> =
  | { status: 200; data: TData }
  | { status: number; data: undefined };

/* A thrown fetch never reaches a status, so give callers one they can branch on. */
const NETWORK_ERROR_STATUS = 0;

export type ObjectCatalogResponse =
  GetApiResponseByUrl<'/v0.1/feeds/{id}/object-catalog'>;
export type ObjectCatalogResult = ApiResult<ObjectCatalogResponse>;
export type ObjectFieldsResult = ApiResult<ObjectFieldsResponse>;

export type CreateAccountProductSourceError = GetApiResponseByStatus<
  '/v0.1/feeds/{id}/sources/account-product',
  'post',
  400
>;

export type CreateAccountProductSourceResult =
  | { status: 200; data: Source; error?: undefined }
  | {
      status: number;
      data: undefined;
      error?: CreateAccountProductSourceError;
    };

export function useAccountProductMappingApi() {
  async function getObjectCatalog(
    feedId: number,
  ): Promise<ObjectCatalogResult> {
    const PATH = '/v0.1/feeds/{id}/object-catalog';
    const options = { params: { path: { id: feedId } } };
    try {
      const { data, response } = await crossbeamApi.GET(PATH, options);
      if (response.status === 200 && data) {
        return { status: 200, data };
      }
      return { status: response.status, data: undefined };
    } catch {
      return { status: NETWORK_ERROR_STATUS, data: undefined };
    }
  }

  async function getObjectFields(
    feedId: number,
    apiName: string,
  ): Promise<ObjectFieldsResult> {
    const PATH = '/v0.1/feeds/{id}/objects/{api_name}/fields';
    const options = { params: { path: { id: feedId, api_name: apiName } } };
    try {
      const { data, response } = await crossbeamApi.GET(PATH, options);
      if (response.status === 200 && data) {
        return { status: 200, data };
      }
      return { status: response.status, data: undefined };
    } catch {
      return { status: NETWORK_ERROR_STATUS, data: undefined };
    }
  }

  async function createAccountProductSource(
    feedId: number,
    body: CreateAccountProductSourceRequest,
  ): Promise<CreateAccountProductSourceResult> {
    const PATH = '/v0.1/feeds/{id}/sources/account-product';
    const options = { params: { path: { id: feedId } }, body };
    try {
      const { data, error, response } = await crossbeamApi.POST(PATH, options);
      if (response.status === 200 && data) {
        return { status: 200, data: data as Source };
      }
      return { status: response.status, data: undefined, error };
    } catch {
      return { status: NETWORK_ERROR_STATUS, data: undefined };
    }
  }

  return { getObjectCatalog, getObjectFields, createAccountProductSource };
}
